<template>
  <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:items-start lg:gap-7">
    <section class="grid gap-5">
      <div class="grid gap-3.5 py-2.5">
        <h3 class="text-[13px] font-medium uppercase tracking-[1px] text-[#ffaa00]">Basic settings</h3>
        <bly-native-text-field
          :value="model.name"
          label="Name"
          :error="errors.name"
          required
          @input="updateRoot('name', $event)"
        />
        <bly-native-textarea-field
          :value="model.description"
          label="Description"
          @input="updateRoot('description', $event)"
        />
        <bly-native-text-field
          v-if="mode === 'create'"
          :value="model.id"
          label="ID"
          placeholder="Optional custom ID"
          @input="updateRoot('id', $event)"
        />
        <bly-native-select-field
          v-if="mode === 'create'"
          :value="model.accountIds"
          label="Accounts"
          hint="Accounts are only mapped during creation, matching the existing behavior."
          :error="errors.accountIds"
          :options="accountOptions"
          multiple
          @input="updateRoot('accountIds', $event)"
        />
      </div>

      <div class="grid gap-3.5 py-2.5">
        <h3 class="text-[13px] font-medium uppercase tracking-[1px] text-[#ffaa00]">Claims</h3>
        <div class="grid gap-1">
          <span class="text-xs font-semibold text-[#dde6f3]">Access claims *</span>
          <el-cascader
            :value="model.claimsCanAccess"
            :options="claimsTree"
            :props="{ multiple: true, checkStrictly: true }"
            :show-all-levels="false"
            collapse-tags
            clearable
            class="w-full"
            @input="updateRoot('claimsCanAccess', $event)"
          />
          <span v-if="errors.claimsCanAccess" class="text-xs text-red-300">
            {{ errors.claimsCanAccess }}
          </span>
        </div>

        <div class="mt-3.5 grid gap-1">
          <span class="text-xs font-semibold text-[#dde6f3]">Edit claims *</span>
          <el-cascader
            :value="model.claimsCanEdit"
            :options="claimsTree"
            :props="{ multiple: true, checkStrictly: true }"
            :show-all-levels="false"
            collapse-tags
            clearable
            class="w-full"
            @input="updateRoot('claimsCanEdit', $event)"
          />
          <span v-if="errors.claimsCanEdit" class="text-xs text-red-300">
            {{ errors.claimsCanEdit }}
          </span>
        </div>
      </div>
    </section>

    <section class="grid gap-5">
      <div class="grid gap-3.5 py-2.5">
        <h3 class="text-[13px] font-medium uppercase tracking-[1px] text-[#ffaa00]">Input configuration</h3>

        <bly-permission-field label="Input type" :error="errors['input.type']" required>
          <bly-pill-selector
            :value="model.input.type"
            label="Input type"
            :options="inputTypeOptions"
            @input="$emit('type-change', $event)"
          />
        </bly-permission-field>

        <bly-native-select-field
          :value="model.input.locationArray"
          label="Locations"
          :options="locationOptions"
          multiple
          @input="updateInput('locationArray', $event)"
        />
        <bly-native-text-field
          :value="model.input.url"
          label="Input URL"
          :disabled="model.input.createInterface"
          @input="updateInput('url', $event)"
        />
        <bly-native-checkbox-field
          :value="!!model.input.frcAvailable"
          label="FRC available"
          @input="updateInput('frcAvailable', $event)"
        />
        <bly-native-checkbox-field
          :value="!!model.input.requireDetails"
          label="Require details"
          @input="updateInput('requireDetails', $event)"
        />
      </div>

      <div v-if="model.input.type === 'ROUTE'" class="grid gap-3.5 py-2.5">
        <h3 class="text-[13px] font-medium uppercase tracking-[1px] text-[#ffaa00]">Stream Router</h3>
        <bly-native-checkbox-field
          :value="!!model.input.createInterface"
          label="Create Stream Router interface"
          @input="updateInput('createInterface', $event)"
        />
        <div v-if="model.input.createInterface" class="grid gap-3">
          <bly-native-select-field
            :value="model.input.streamRouterId"
            label="Stream Router"
            :error="errors['input.streamRouterId']"
            :options="streamRouterOptions"
            placeholder="Select Stream Router"
            required
            @input="updateInput('streamRouterId', $event)"
          />
          <bly-native-text-field
            :value="model.input.interfaceName"
            label="Interface name"
            :error="errors['input.interfaceName']"
            required
            @input="updateInput('interfaceName', $event)"
          />
          <stream-router-interface-config :value="model.input" :stream-routers="streamRouters" @input="updateInputModel" />
        </div>
      </div>

      <div v-if="model.input.type === 'MEDIA_CONNECT'" class="grid gap-3.5 py-2.5">
        <h3 class="text-[13px] font-medium uppercase tracking-[1px] text-[#ffaa00]">MediaConnect</h3>
        <bly-native-text-field
          :value="model.input.mcInputName"
          label="Name"
          :error="errors['input.mcInputName']"
          required
          @input="updateInput('mcInputName', $event)"
        />
        <bly-native-text-field
          :value="model.input.mcInputBitrate"
          type="number"
          label="Maximum bitrate"
          :error="errors['input.mcInputBitrate']"
          required
          @input="updateInput('mcInputBitrate', $event)"
        />
        <bly-native-select-field
          :value="model.input.mcInputRoutingScope"
          label="Routing scope"
          :error="errors['input.mcInputRoutingScope']"
          :options="routingScopeOptions"
          required
          @input="updateInput('mcInputRoutingScope', $event)"
        />
        <bly-native-select-field
          :value="model.input.mcInputTier"
          label="Tier"
          :error="errors['input.mcInputTier']"
          :options="tierInputOptions"
          required
          @input="updateInput('mcInputTier', $event)"
        />
        <bly-native-select-field
          :value="model.input.mcInputNetworkInterfaceArn"
          label="Network interface"
          :error="errors['input.mcInputNetworkInterfaceArn']"
          :options="networkInterfaceOptions"
          required
          @input="updateInput('mcInputNetworkInterfaceArn', $event)"
        />
        <bly-native-select-field
          :value="model.input.mcInputProtocol"
          label="Protocol"
          :error="errors['input.mcInputProtocol']"
          :options="protocolOptions"
          required
          @input="updateInput('mcInputProtocol', $event)"
        />

        <template v-if="model.input.mcInputProtocol === 'SRT_LISTENER'">
          <bly-native-text-field
            :value="model.input.mcInputPort"
            type="number"
            label="Port"
            :error="errors['input.mcInputPort']"
            required
            @input="updateInput('mcInputPort', $event)"
          />
          <bly-native-text-field
            :value="model.input.mcInputLatency"
            type="number"
            label="Latency (ms)"
            :error="errors['input.mcInputLatency']"
            required
            @input="updateInput('mcInputLatency', $event)"
          />
        </template>

        <template v-if="model.input.mcInputProtocol === 'SRT_CALLER'">
          <bly-native-text-field
            :value="model.input.mcInputSourceAddress"
            label="Source address"
            :error="errors['input.mcInputSourceAddress']"
            required
            @input="updateInput('mcInputSourceAddress', $event)"
          />
          <bly-native-text-field
            :value="model.input.mcInputSourcePort"
            type="number"
            label="Source port"
            :error="errors['input.mcInputSourcePort']"
            required
            @input="updateInput('mcInputSourcePort', $event)"
          />
          <bly-native-text-field
            :value="model.input.mcInputLatency"
            type="number"
            label="Latency (ms)"
            :error="errors['input.mcInputLatency']"
            required
            @input="updateInput('mcInputLatency', $event)"
          />
        </template>

        <template v-if="model.input.mcInputProtocol === 'RTP'">
          <bly-native-text-field
            :value="model.input.mcInputPort"
            type="number"
            label="Port"
            :error="errors['input.mcInputPort']"
            required
            @input="updateInput('mcInputPort', $event)"
          />
        </template>

        <template v-if="model.input.mcInputProtocol === 'RIST'">
          <bly-native-text-field
            :value="model.input.mcInputPort"
            type="number"
            label="Port"
            :error="errors['input.mcInputPort']"
            required
            @input="updateInput('mcInputPort', $event)"
          />
          <bly-native-text-field
            :value="model.input.mcInputRecoveryLatency"
            type="number"
            label="Recovery latency (ms)"
            :error="errors['input.mcInputRecoveryLatency']"
            required
            @input="updateInput('mcInputRecoveryLatency', $event)"
          />
        </template>
      </div>
    </section>
  </div>
</template>

<script>
import StreamRouterInterfaceConfig from "@/components/StreamRouter/StreamRouterInterfaceConfig.vue";

const MODAL_INPUT_TYPES = ["ROUTE", "TECHEX", "MEDIA_CONNECT"];
const ROUTE_LOCATION_CODES = ["IMG-UK"];
const TECHEX_LOCATION_CODES = ["LON1", "LON2", "HGK1", "SGP1"];

export default {
  name: "InputPermissionForm",
  components: {
    StreamRouterInterfaceConfig,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    mode: {
      type: String,
      default: "create",
    },
    accounts: {
      type: Array,
      default: () => [],
    },
    claimsTree: {
      type: Array,
      default: () => [],
    },
    inputTypes: {
      type: Array,
      default: () => [],
    },
    locations: {
      type: Array,
      default: () => [],
    },
    streamRouters: {
      type: Array,
      default: () => [],
    },
    mediaConnectRoutingScopes: {
      type: Array,
      default: () => [],
    },
    mediaConnectTiersInput: {
      type: Array,
      default: () => [],
    },
    mediaConnectProtocols: {
      type: Array,
      default: () => [],
    },
    mediaConnectNetworkInterfaces: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    model() {
      return this.value;
    },
    accountOptions() {
      return this.accounts.map((account) => ({
        value: account.id,
        label: `${account.client.name} > ${account.name}`,
      }));
    },
    inputTypeOptions() {
      const supportedTypes = this.inputTypes.filter((type) => MODAL_INPUT_TYPES.includes(type));
      const options = this.model?.input?.type && !supportedTypes.includes(this.model.input.type)
        ? [...supportedTypes, this.model.input.type]
        : supportedTypes;
      return options.map((type) => ({ value: type, label: type.replace("_", " ") }));
    },
    locationOptions() {
      if (this.model?.input?.type === "ROUTE") {
        return this.locations
          .filter((location) => ROUTE_LOCATION_CODES.includes(location.code))
          .map((location) => ({
            value: location.code,
            label: `${location.name} (${location.code})`,
          }));
      }

      if (this.model?.input?.type === "TECHEX") {
        return this.locations
          .filter((location) => TECHEX_LOCATION_CODES.includes(location.code))
          .map((location) => ({
            value: location.code,
            label: `${location.name} (${location.code})`,
          }));
      }

      if (this.model?.input?.type === "MEDIA_CONNECT") {
        const regionCodes = new Set(
          this.mediaConnectNetworkInterfaces.map((item) => item.regionName).filter(Boolean)
        );
        (this.model.input.locationArray || []).forEach((locationCode) => regionCodes.add(locationCode));
        return [...regionCodes].sort().map((code) => ({
          value: code,
          label: code,
        }));
      }

      return this.locations.map((location) => ({
        value: location.code,
        label: `${location.name} (${location.code})`,
      }));
    },
    streamRouterOptions() {
      return this.streamRouters.map((router) => ({ value: router.id, label: router.name }));
    },
    routingScopeOptions() {
      return this.mediaConnectRoutingScopes.map((scope) => ({ value: scope, label: scope }));
    },
    tierInputOptions() {
      return this.mediaConnectTiersInput.map((tier) => ({ value: tier, label: tier }));
    },
    protocolOptions() {
      return this.mediaConnectProtocols.map((protocol) => ({ value: protocol, label: protocol }));
    },
    networkInterfaceOptions() {
      return this.mediaConnectNetworkInterfaces.map((item) => ({
        value: item.networkInterfaceArn,
        label: item.name,
      }));
    },
  },
  methods: {
    emitModel(nextModel) {
      this.$emit("input", nextModel);
    },
    updateRoot(field, value) {
      this.emitModel({
        ...this.model,
        [field]: value,
      });
    },
    updateInput(field, value) {
      this.emitModel({
        ...this.model,
        input: {
          ...this.model.input,
          [field]: value,
        },
      });
    },
    updateInputModel(nextInputModel) {
      this.emitModel({
        ...this.model,
        input: nextInputModel,
      });
    },
  },
};
</script>
